import React from 'react';
import PropTypes from 'prop-types';
import Box from '@material-ui/core/Box';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';
import Typography from '@material-ui/core/Typography';
import DarkCover from './DarkCover';
import CardHeader from './CardHeader';
import Card from './Card';
import Calories from 'menus/components/Calories';
import Dotdotdot from 'react-dotdotdot';
import { getImageWithHeight } from 'utils/images';

import { getItemDetailString } from 'menus/utils/getItemDetailString';

const FULL_HEIGHT = { height: '100%' };

const useSoldOutStyles = makeStyles(theme => ({
  root: {
    color: theme.palette.primary.contrastText
  }
}));

const getTypeVariant = isSoldOut => (isSoldOut ? 'body2' : 'body1');

const getPrice = (remainingInventoryInCart, isSoldOut, price) => {
  if (remainingInventoryInCart) {
    return 'Last Items In Cart';
  }

  if (isSoldOut) {
    return 'Sold Out';
  }

  return `$${price}`;
};
const MenuItemCard = ({
  spicy,
  vegetarian,
  vegan,
  kosher,
  halal,
  organic,
  name,
  description,
  image,
  calories,
  price,
  inventoryLessCart,
  handleClick,
  inventory
}) => {
  const detail = getItemDetailString([
    { Spicy: spicy },
    { Vegan: vegan },
    { Vegetarian: vegetarian },
    { Kosher: kosher },
    { Halal: halal },
    { Organic: organic }
  ]);

  const isSoldOut = inventory === 0;

  const remainingInventoryIsInCart =
    !isSoldOut && inventoryLessCart !== null && inventoryLessCart <= 0;
  const typeVariant = getTypeVariant(isSoldOut || remainingInventoryIsInCart);
  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const soldOutClasses = useSoldOutStyles();
  const textContentGridStyles = image ? { lg: 8, md: 7, xs: 12 } : { xs: 12 };
  const textContentFlushBottomStyles = {
    marginTop: image ? 0 : '8px'
  };
  const resizedImage = getImageWithHeight(image, 344);
  const shouldDisplayInventoryLeftMessage =
    !!inventoryLessCart && inventoryLessCart < 5 && inventoryLessCart > 0;
  return (
    <Card
      disabled={isSoldOut || remainingInventoryIsInCart}
      handleClick={handleClick}
      role="menuitem"
      dataTestId="menu-page-item"
    >
      <Grid container style={FULL_HEIGHT}>
        <Grid item {...textContentGridStyles} style={FULL_HEIGHT}>
          <CardContent style={{ maxHeight: isSmallAndDown ? 'auto' : '172px' }}>
            <Box
              display={isSmallAndDown ? 'block' : 'flex'}
              flexDirection="column"
              {...FULL_HEIGHT}
              justifyContent="space-between"
            >
              <div>
                <CardHeader title={name} disabled={isSoldOut} />
                {description && (
                  <div style={{ marginTop: '6px' }}>
                    {/* Typography component set as span to avoid errors of
                    div as descendant of p */}
                    <Typography component="span" variant={typeVariant}>
                      <Dotdotdot clamp={2}>{description}</Dotdotdot>
                    </Typography>
                  </div>
                )}
                {detail && (
                  <div style={{ marginTop: '4px' }}>
                    <Typography component="p" variant={typeVariant}>
                      {detail}
                    </Typography>
                  </div>
                )}
              </div>
              <div style={textContentFlushBottomStyles}>
                {calories && (
                  <Calories calories={calories} typeVariant={typeVariant} />
                )}
                <Typography component="p" color="textPrimary">
                  {getPrice(remainingInventoryIsInCart, isSoldOut, price)}
                  {shouldDisplayInventoryLeftMessage && (
                    <Typography
                      data-testid="menu-item-inventory-left"
                      component="span"
                    >
                      {` - only ${inventoryLessCart} left`}
                    </Typography>
                  )}
                </Typography>
              </div>
            </Box>
          </CardContent>
        </Grid>
        {image && (
          <Grid
            item
            lg={4}
            md={5}
            xs={12}
            style={{
              position: 'relative',
              order: isSmallAndDown ? -1 : 0
            }}
          >
            <CardMedia
              component="img"
              alt={name}
              image={resizedImage}
              title={name}
              height={172}
            />

            {isSoldOut && (
              <DarkCover>
                <Typography align="center" classes={soldOutClasses}>
                  Sold Out
                </Typography>
              </DarkCover>
            )}
            {remainingInventoryIsInCart && (
              <DarkCover>
                <Typography align="center" classes={soldOutClasses}>
                  Last Items In Cart
                </Typography>
              </DarkCover>
            )}
          </Grid>
        )}
      </Grid>
    </Card>
  );
};

MenuItemCard.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string,
  image: PropTypes.string,
  calories: PropTypes.string,
  price: PropTypes.string.isRequired,
  spicy: PropTypes.bool,
  vegetarian: PropTypes.bool,
  vegan: PropTypes.bool,
  kosher: PropTypes.bool,
  halal: PropTypes.bool,
  organic: PropTypes.bool,
  handleClick: PropTypes.func,
  inventory: PropTypes.number,
  inventoryLessCart: PropTypes.number
};

export default MenuItemCard;
